import {
  fetchPurchasedProducts,
  getCustomerIdOrThrow,
  hasCustomerId,
} from "@/lib/api";
import { ROSE_MIST_PRODUCT_ID } from "./config";

const CACHE_PREFIX = "so_fills_upsell_eligible:";

function cacheKey(customerId: number): string {
  return `${CACHE_PREFIX}${customerId}`;
}

/**
 * Synchronous eligibility check used at add-to-cart time. Reads only the
 * sessionStorage cache written by `prefetchFillsEligibility` — a missing
 * cache entry (fetch failed, still in flight, anonymous visitor) means
 * ineligible, so the add is never delayed.
 */
export function isFillsUpsellEligible(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (!hasCustomerId()) return false;
    const id = getCustomerIdOrThrow();
    return sessionStorage.getItem(cacheKey(id)) === "1";
  } catch {
    return false;
  }
}

let prefetchStarted = false;

/**
 * Fire-and-forget: resolves whether this customer ever bought Rose Mist
 * (backend product id 45) via the purchased-products endpoint and caches the
 * answer per customer for the session.
 */
export function prefetchFillsEligibility(): void {
  if (typeof window === "undefined" || prefetchStarted) return;
  try {
    if (!hasCustomerId()) return;
    prefetchStarted = true;
    const id = getCustomerIdOrThrow();
    if (sessionStorage.getItem(cacheKey(id)) != null) return;
    void fetchPurchasedProducts()
      .then((result) => {
        const bought = result.products.some(
          (p) =>
            typeof p === "object" &&
            p !== null &&
            (p as { id?: unknown }).id === ROSE_MIST_PRODUCT_ID,
        );
        sessionStorage.setItem(cacheKey(id), bought ? "1" : "0");
      })
      .catch(() => {
        /* leave cache empty — treated as ineligible */
      });
  } catch {
    /* no customer id resolvable — anonymous stays ineligible */
  }
}
